import React, { useEffect, useRef } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, CheckCircle2 } from 'lucide-react';

export function Hero() {
  const { t } = useLanguage();
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const elements = entry.target.querySelectorAll('.animated-element');
            elements.forEach((el, i) => {
              setTimeout(() => {
                el.classList.add('appear');
              }, i * 100);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (heroRef.current) {
      observer.observe(heroRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={heroRef} className="pt-28 pb-12 md:pt-32 md:pb-16">
      <div className="container px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Hero content */}
          <div className="lg:col-span-2 bg-card rounded-[2rem] p-8 md:p-12 lg:p-16 flex flex-col justify-center border border-border shadow-sm animated-element">
            <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-display font-bold leading-[1.05] tracking-tight mb-6 text-balance">
              {t('hero.title').replace('Coaching Business', '')}
              <span className="text-accent">Coaching</span> Business
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-xl mb-8 leading-relaxed">
              {t('hero.subtitle')}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/register">
                <Button size="lg" className="rounded-full px-8 py-6 font-semibold text-lg group">
                  {t('hero.cta')}
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="rounded-full px-8 py-6 font-semibold text-lg border-2 border-foreground hover:bg-secondary">
                  {t('common.learnMore')}
                </Button>
              </Link>
            </div>
          </div>

          {/* Floating notifications */}
          <div className="bg-accent rounded-[2rem] p-8 flex flex-col items-center justify-center relative overflow-hidden min-h-[360px] animated-element">
            <div className="absolute inset-0 opacity-10">
              <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
                <path d="M0 0 L100 100 M100 0 L0 100" stroke="white" strokeWidth="0.5" />
              </svg>
            </div>
            <div className="relative z-10 space-y-4 w-full max-w-xs">
              <div className="bg-white/95 backdrop-blur shadow-xl rounded-2xl p-4 transform -rotate-2">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center text-accent font-bold">
                    <Calendar className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-bold">{t('hero.newBooking')}</p>
                    <p className="text-xs text-muted-foreground">{t('hero.bookingTime')}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white/95 backdrop-blur shadow-xl rounded-2xl p-4 transform translate-x-4 rotate-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                    <CheckCircle2 className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-bold">{t('hero.sessionComplete')}</p>
                    <p className="text-xs text-muted-foreground">{t('hero.progressUpdated')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
